// shop-frontend/src/pages/ProductDetailsUnified.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { trackProductView } from "../utils/api";
import ProductReviews from "../components/ProductReviews";
import { SIZE_RE } from "../utils/sizeUtils";
import "../styles/productDetailsUnified.css";

/* =========================
   API HOST (safe)
   ========================= */
const API_HOST_RAW =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  "https://moondala-one.onrender.com";

function normalizeBase(u) {
  const s = String(u || "").trim();
  if (!s) return "https://moondala-one.onrender.com";
  return s.endsWith("/") ? s.slice(0, -1) : s;
}
const API_HOST = normalizeBase(API_HOST_RAW);

/* =========================
   Helpers
   ========================= */
function money(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0.00";
  return x.toFixed(2);
}

function cleanId(v) {
  const s = String(v || "").replace(/[<>]/g, "").trim();
  if (!s) return "";
  const noColon = s.startsWith(":") ? s.slice(1) : s;
  if (!noColon) return "";
  const low = noColon.toLowerCase();
  if (low === "id" || low === "productid") return "";
  return noColon;
}

function safeNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function makeAbsolute(url) {
  if (!url) return "";
  const s = String(url).trim();
  if (!s) return "";
  if (s.startsWith("http://") || s.startsWith("https://")) return s;

  if (s.startsWith("/")) return `${API_HOST}${s}`;
  if (s.startsWith("uploads/")) return `${API_HOST}/${s}`;
  if (!s.includes("/")) return `${API_HOST}/uploads/${s}`;
  return `${API_HOST}/${s}`;
}

function pickImages(p) {
  const arr = [];
  if (Array.isArray(p?.variants)) {
    for (const variant of p.variants) {
      if (Array.isArray(variant?.images)) {
        arr.push(...variant.images);
      }
    }
  }
  if (p?.image) arr.push(p.image);
  if (Array.isArray(p?.images)) arr.push(...p.images);
  if (Array.isArray(p?.displayImages)) arr.push(...p.displayImages);
  if (p?.media?.image) arr.push(p.media.image);
  if (Array.isArray(p?.media?.images)) arr.push(...p.media.images);

  return Array.from(new Set(arr.filter(Boolean).map(makeAbsolute).filter(Boolean)));
}

function idFromPath(pathname) {
  const p = String(pathname || "");
  const m = p.match(/\/(products?|product|item|p)\/([^/?#]+)/i);
  return m && m[2] ? cleanId(m[2]) : "";
}

/* =========================
   Fetch helpers (NO request() redirects) */
/* =========================
   Fetch helpers (NO request() redirects)
   ========================= */
async function fetchJson(url, opts) {
  const res = await fetch(url, opts);
  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json")
    ? await res.json().catch(() => ({}))
    : { message: await res.text().catch(() => "") };

  if (!res.ok) throw new Error(data?.message || `Request failed (${res.status})`);
  return data;
}

function getShopToken() {
  return (
    localStorage.getItem("shopToken") ||
    localStorage.getItem("shop_token") ||
    localStorage.getItem("token") ||
    ""
  );
}

async function fetchAuthJson(pathOrUrl) {
  const token = getShopToken();
  const url = String(pathOrUrl || "");
  const full = url.startsWith("http")
    ? url
    : `${API_HOST}${url.startsWith("/") ? "" : "/"}${url}`;

  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  return fetchJson(full, { headers });
}

/* =========================
   Component
   ========================= */
export default function ProductDetailsUnified({ mode = "shop" }) {
  const nav = useNavigate();
  const params = useParams();
  const loc = useLocation();
  const [searchParams] = useSearchParams();

  // Path may be "parentId__variantId" — split so the API call uses a valid ObjectId,
  // and the second part is used to pre-select that variant on load.
  const rawIdParam = useMemo(() => {
    return cleanId(params.id) || cleanId(params.productId) || idFromPath(loc.pathname);
  }, [params.id, params.productId, loc.pathname]);
  const [id, _preSelectedVid] = useMemo(() => {
    const s = String(rawIdParam || "");
    const parts = s.split("__");
    return [parts[0] || "", parts[1] || ""];
  }, [rawIdParam]);

  const shopIdFromUrl = useMemo(() => cleanId(params.shopId), [params.shopId]);

  // URL-passed discount info (when arriving from a discounted card link)
  const sidebarDiscountPct = Math.min(100, Math.max(0, Number(searchParams.get("discount") || 0)));
  const discountedVariantIds = searchParams.get("dvids")
    ? new Set(searchParams.get("dvids").split(","))
    : null;

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [product, setProduct] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [_shop, setShop] = useState(null);
  const [qty, setQty] = useState(1);
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  // Authoritative per-variant discount map fetched from the shop's customSidebar
  // Keys: "productId" | "productId__variantId" → discount % (1-100)
  const [shopDiscountMap, setShopDiscountMap] = useState({});

  const [activeImg, setActiveImg] = useState("");
  const handleImgHalfClick = (e, imgs) => {
    if (!imgs || imgs.length < 2) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const idx = imgs.indexOf(activeImg);
    if (x < rect.width / 2) {
      setActiveImg(imgs[(idx - 1 + imgs.length) % imgs.length]);
    } else {
      setActiveImg(imgs[(idx + 1) % imgs.length]);
    }
  };

  const backTo =
    loc.state?.backTo ||
    (mode === "public"
      ? shopIdFromUrl
        ? `/s/${shopIdFromUrl}`
        : "/"
      : "/shop/mall/preview");

  async function load() {
    setLoading(true);
    setErr("");
    setProduct(null);
    setShop(null);

    try {
      if (!id) {
        throw new Error("Missing product id. Your product link must be like /shop/product/:id");
      }

      // ---------- PUBLIC ----------
      if (mode === "public") {
        if (shopIdFromUrl) {
          const data = await fetchJson(`${API_HOST}/api/public/shops/${shopIdFromUrl}`);
          const products = Array.isArray(data?.products) ? data.products : [];
          const found = products.find((p) => String(p?._id || p?.id) === String(id));
          if (!found) throw new Error("Product not found");

          setShop(data?.shop || null);
          setProduct(found);

          const imgs = pickImages(found);
          setActiveImg(imgs[0] || "");
          setQty(1);
          return;
        }

        const endpoints = [
          `${API_HOST}/api/products/${id}`,
          `${API_HOST}/api/shop-products/${id}`,
        ];

        let pdata = null;
        let lastErr = null;

        for (const url of endpoints) {
          try {
            pdata = await fetchJson(url);
            break;
          } catch (e) {
            lastErr = e;
          }
        }

        const p = pdata?.product || pdata?.data?.product || pdata;
        if (!p?._id && !p?.id) throw new Error(lastErr?.message || "Product not found");

        setProduct(p);
        setShop(pdata?.shop || null);

        const imgs = pickImages(p);
        setActiveImg(imgs[0] || "");
        setQty(1);

        // ✅ Track this view
        try {
          if (p?._id) {
            trackProductView(p._id).catch(() => {});
          }
        } catch {}
        return;
      }

      // ---------- AUTH (SHOP APP) ----------
      const endpoints = [
        `/api/shop/products/${id}`,
        `/api/shops/products/${id}`,
        `/api/products/${id}`,
        `/api/shop-products/${id}`,
      ];

      let res = null;
      let lastErr = null;

      for (const path of endpoints) {
        try {
          res = await fetchAuthJson(path);
          break;
        } catch (e) {
          lastErr = e;
        }
      }

      const found = res?.product || res?.data?.product || res?.data || res;
      if (!found?._id && !found?.id) throw new Error(lastErr?.message || "Product not found");

      setProduct(found);
      setShop(res?.shop || found?.shop || null);

      const imgs = pickImages(found);
      setActiveImg(imgs[0] || "");
      setQty(1);

      // ✅ Track this view
      try {
        if (found?._id) {
          trackProductView(found._id).catch(() => {});
        }
      } catch {}

      // ✅ removed /api/products/:id/view to stop 404 spam
    } catch (e) {
      setErr(e?.message || "Failed to load product");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, shopIdFromUrl, mode]);

  // ===== Variants =====
  const variants = useMemo(
    () => Array.isArray(product?.variants)
      ? product.variants.filter((v) => v && v.isActive !== false)
      : [],
    [product]
  );

  // Detect the non-size grouping option (colour / material / style)
  // by scanning option values — matches expandProductsToVariants behaviour.
  const colorOptionName = useMemo(() => {
    const optionMap = {};
    for (const v of variants) {
      for (const ov of (v.optionValues || [])) {
        if (!ov?.name) continue;
        if (!optionMap[ov.name]) optionMap[ov.name] = new Set();
        optionMap[ov.name].add(String(ov.value || "").trim());
      }
    }
    const looksLikeSize = (vals) => [...vals].every((v) => SIZE_RE.test(String(v).trim()));
    for (const name of Object.keys(optionMap)) {
      if (/^colou?r$/i.test(name)) return name;
    }
    for (const [name, vals] of Object.entries(optionMap)) {
      if (!looksLikeSize(vals)) return name;
    }
    return "";
  }, [variants]);

  const sizeOptionName = useMemo(() => {
    const optionMap = {};
    for (const v of variants) {
      for (const ov of (v.optionValues || [])) {
        if (!ov?.name || ov.name === colorOptionName) continue;
        if (!optionMap[ov.name]) optionMap[ov.name] = new Set();
        optionMap[ov.name].add(String(ov.value || "").trim());
      }
    }
    if (optionMap["size"]) return "size";
    if (optionMap["Size"]) return "Size";
    const looksLikeSize = (vals) => [...vals].every((v) => SIZE_RE.test(String(v).trim()));
    for (const [name, vals] of Object.entries(optionMap)) {
      if (looksLikeSize(vals)) return name;
    }
    return "";
  }, [variants, colorOptionName]);

  const colorValues = useMemo(() => {
    if (!colorOptionName) return [];
    return [...new Set(
      variants
        .map((v) => (v.optionValues || []).find((ov) => ov.name === colorOptionName)?.value || "")
        .filter(Boolean)
    )];
  }, [variants, colorOptionName]);

  // Show only images for the selected color; fall back to product-level images
  const images = useMemo(() => {
    if (!product) return [];
    const allVariants = Array.isArray(product.variants) ? product.variants : [];
    if (selectedColor && colorOptionName) {
      const colorVariants = allVariants.filter((v) =>
        ((v.optionValues || []).find((ov) => ov.name === colorOptionName)?.value || "")
          .toLowerCase() === selectedColor.toLowerCase()
      );
      const colorImgs = colorVariants.flatMap((v) => Array.isArray(v.images) ? v.images : []);
      const clean = Array.from(new Set(colorImgs.filter(Boolean).map(makeAbsolute).filter(Boolean)));
      if (clean.length) return clean;
    }
    // Fallback: product-level images only (no all-variant mixing)
    const arr = [];
    if (product.image) arr.push(product.image);
    if (Array.isArray(product.images)) arr.push(...product.images);
    if (Array.isArray(product.displayImages)) arr.push(...product.displayImages);
    if (product.media?.image) arr.push(product.media.image);
    if (Array.isArray(product.media?.images)) arr.push(...product.media.images);
    return Array.from(new Set(arr.filter(Boolean).map(makeAbsolute).filter(Boolean)));
  }, [product, selectedColor, colorOptionName]);

  const sizeValues = useMemo(() => {
    if (!sizeOptionName) return [];
    const subset = (colorOptionName && selectedColor)
      ? variants.filter((v) =>
          ((v.optionValues || []).find((ov) => ov.name === colorOptionName)?.value || "")
            .toLowerCase() === selectedColor.toLowerCase()
        )
      : variants;
    return [...new Set(
      subset
        .map((v) => (v.optionValues || []).find((ov) => ov.name === sizeOptionName)?.value || "")
        .filter(Boolean)
    )];
  }, [variants, colorOptionName, sizeOptionName, selectedColor]);

  const selectedVariant = useMemo(() => {
    if (!variants.length) return null;
    return variants.find((v) => {
      const c = (v.optionValues || []).find((ov) => ov.name === colorOptionName)?.value || "";
      const s = (v.optionValues || []).find((ov) => ov.name === sizeOptionName)?.value || "";
      const colorOk = !colorOptionName || !selectedColor || c.toLowerCase() === selectedColor.toLowerCase();
      const sizeOk = !sizeOptionName || !selectedSize || s.toLowerCase() === selectedSize.toLowerCase();
      return colorOk && sizeOk;
    }) || null;
  }, [variants, selectedColor, selectedSize, colorOptionName, sizeOptionName]);

  // Pre-select variant from URL (parentId__variantId). Runs after product/variants are ready.
  useEffect(() => {
    if (!product || !variants.length) return;
    const wantVid = String(_preSelectedVid || searchParams.get("variantId") || "");
    if (!wantVid) return;
    const v = variants.find((x) => String(x._id) === wantVid);
    if (!v) return;
    const c = (v.optionValues || []).find((ov) => ov.name === colorOptionName)?.value || "";
    const s = (v.optionValues || []).find((ov) => ov.name === sizeOptionName)?.value || "";
    if (c) setSelectedColor(c);
    if (s) setSelectedSize(s);
    // Switch main image to this variant's image (fallback to any same-colour image)
    let img = (Array.isArray(v.images) && v.images[0]) || "";
    if (!img && c && colorOptionName) {
      const sameColour = variants.find((x) =>
        ((x.optionValues || []).find((ov) => ov.name === colorOptionName)?.value || "")
          .toLowerCase() === c.toLowerCase() && Array.isArray(x.images) && x.images[0]
      );
      if (sameColour) img = sameColour.images[0];
    }
    if (img) setActiveImg(makeAbsolute(typeof img === "string" ? img : (img?.url || img?.path || "")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, variants, colorOptionName, sizeOptionName]);

  // When color-aware image list changes, reset main image if the current one is no longer in it
  useEffect(() => {
    if (images.length > 0 && !images.includes(activeImg)) {
      setActiveImg(images[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  // Fetch shop's customSidebar to know every discounted variant of this product
  // (covers ALL colours/sizes regardless of which link the user clicked).
  const productShopId = product?.shopId || product?.shop?._id || product?.shop?.id || shopIdFromUrl;
  useEffect(() => {
    if (!productShopId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJson(
          `${API_HOST}/api/public/shops/${encodeURIComponent(productShopId)}/mall`
        );
        const sidebar = data?.page?.customSidebar || data?.mallPage?.customSidebar || [];
        const map = {};
        const set = (k, pct) => { if (k && (!map[k] || map[k] < pct)) map[k] = pct; };
        for (const ind of sidebar) {
          for (const cat of (ind.categories || [])) {
            const pct = Number(cat.discount) || 0;
            if (pct <= 0) continue;
            for (const pid of (cat.productIds || [])) set(pid, pct);
          }
        }
        if (!cancelled) setShopDiscountMap(map);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [productShopId]);

  // ===== Derived =====
  const title = product?.title || product?.name || "Product";
  const category =
    product?.categoryLabel || product?.category || product?.categoryName || "Category";

  const currency = String(product?.currency || "USD").toUpperCase();
  const rawPrice = selectedVariant?.price
    ?? product?.price
    ?? product?.amount
    ?? product?.salePrice
    ?? product?.cost
    ?? product?.localPrice
    ?? 0;

  const _pidForDiscount = String(product?._id || product?.id || id || "");
  const selectedVid = String(selectedVariant?._id || "");

  // Resolve discount for the CURRENTLY selected variant — customSidebar is the sole source of truth
  const variantDiscountPct = (() => {
    let pct = 0;
    if (selectedVid && shopDiscountMap[`${_pidForDiscount}__${selectedVid}`]) {
      pct = Math.max(pct, shopDiscountMap[`${_pidForDiscount}__${selectedVid}`]);
    }
    if (shopDiscountMap[_pidForDiscount]) {
      pct = Math.max(pct, shopDiscountMap[_pidForDiscount]);
    }
    if (pct === 0 && sidebarDiscountPct > 0) {
      if (discountedVariantIds === null) pct = sidebarDiscountPct;
      else if (selectedVid && discountedVariantIds?.has(selectedVid)) pct = sidebarDiscountPct;
    }
    return Math.min(100, pct);
  })();

  const thisVariantDiscounted = variantDiscountPct > 0;
  const price = thisVariantDiscounted
    ? Math.round(rawPrice * (1 - variantDiscountPct / 100) * 100) / 100
    : rawPrice;

  // Helper: is a specific variant discounted? (used to mark size buttons)
  const isVariantDiscounted = (v) => {
    if (!v) return false;
    if (shopDiscountMap[_pidForDiscount]) return true;
    return shopDiscountMap[`${_pidForDiscount}__${String(v._id || "")}`] > 0;
  };
  // % for a specific variant (used for size badge text)
  const variantPctFor = (v) => {
    if (!v) return 0;
    return Math.max(
      shopDiscountMap[`${_pidForDiscount}__${String(v._id || "")}`] || 0,
      shopDiscountMap[_pidForDiscount] || 0,
      sidebarDiscountPct || 0
    );
  };

  const qtySafe = Math.max(1, Math.min(99, Math.floor(safeNum(qty, 1))));
  const totalValue = safeNum(price, 0) * qtySafe;

  // ===== Actions =====
  function dec() {
    setQty((q) => Math.max(1, Math.min(99, safeNum(q, 1) - 1)));
  }

  function inc() {
    setQty((q) => Math.max(1, Math.min(99, safeNum(q, 1) + 1)));
  }

  // ===== Render =====
  if (loading) {
    return (
      <div className="pdu-wrap">
        <div className="pdu-card" style={{ padding: 20 }}>
          Loading...
          <div style={{ opacity: 0.7, marginTop: 6, fontSize: 12 }}>
            Product ID: {id || "(missing)"}
          </div>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="pdu-wrap">
        <button className="pdu-back" onClick={() => nav(backTo)}>
          ← Back
        </button>
        <div className="pdu-card" style={{ marginTop: 12, padding: 20 }}>
          <div style={{ color: "#ff8080", fontWeight: 900 }}>Error</div>
          <div style={{ marginTop: 8 }}>{err}</div>
          <div style={{ opacity: 0.7, marginTop: 10, fontSize: 12 }}>
            URL: {loc.pathname}
          </div>
        </div>
      </div>
    );
  }

  const pid = String(product?._id || product?.id || "").trim();

  return (
    <div className="pdu-wrap">
      {/* ✅ Top Navigation Bar */}
      <div className="pdu-topbar">
        <div className="pdu-breadcrumb" />

        <div className="pdu-actions">
          <button className="pdu-pill" onClick={() => nav("/shop/products")}>
            Back to Products
          </button>
          <button className="pdu-pill pdu-pill-edit" onClick={() => nav(`/shop/dashboard/edit-product/${pid}`)}>
            Edit Product
          </button>
        </div>
      </div>

      <div className="pdu-card">
        <div className="pdu-left">
          <div
            className="pdu-imgbox"
            style={{ position: "relative", cursor: images.length > 1 ? "pointer" : "default" }}
            onClick={(e) => handleImgHalfClick(e, images)}
          >
            {activeImg ? <img src={activeImg} alt={title} /> : <div>No image</div>}
            {images.length > 1 && (
              <>
                <div style={{ position: "absolute", inset: "0 50% 0 0", display: "flex", alignItems: "center", paddingLeft: 10, opacity: 0, transition: "opacity .2s", pointerEvents: "none" }}
                  className="img-nav-hint-left">
                  <span style={{ background: "rgba(0,0,0,0.45)", color: "#fff", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>‹</span>
                </div>
                <div style={{ position: "absolute", inset: "0 0 0 50%", display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 10, opacity: 0, transition: "opacity .2s", pointerEvents: "none" }}
                  className="img-nav-hint-right">
                  <span style={{ background: "rgba(0,0,0,0.45)", color: "#fff", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>›</span>
                </div>
              </>
            )}
          </div>

          {images.length > 1 && (
            <div className="pdu-thumbs">
              {images.slice(0, 8).map((src) => (
                <button
                  key={src}
                  type="button"
                  className="pdu-thumb"
                  onClick={() => setActiveImg(src)}
                  title="View image"
                  style={{ background: "transparent", border: "none", padding: 0 }}
                >
                  <img src={src} alt="thumb" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="pdu-right">
          <h1 className="pdu-title">{title}</h1>

          <div className="pdu-meta">
            <div style={{ fontWeight: 900, opacity: 0.85 }}>Category</div>
            <div style={{ fontWeight: 1000 }}>{category}</div>
          </div>

          <div className="pdu-row">
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 10 }}>
              <div className="pdu-price" style={{ color: thisVariantDiscounted ? "#ef4444" : undefined }}>
                {currency} {money(price)}
              </div>
              {thisVariantDiscounted && (
                <>
                  <span style={{ fontSize: 16, opacity: 0.7, textDecoration: "line-through" }}>
                    {currency} {money(rawPrice)}
                  </span>
                  <span style={{
                    fontSize: 12, fontWeight: 800, color: "#fff",
                    background: "#f97316", padding: "2px 8px", borderRadius: 8,
                  }}>
                    {variantDiscountPct}% OFF
                  </span>
                </>
              )}
            </div>
            <div style={{ fontWeight: 900, opacity: 0.9 }}>
              Total: {currency} {money(totalValue)}
            </div>
          </div>

          {/* Colour Selector */}
          {colorValues.length > 0 && (
            <div className="pdu-row" style={{ marginTop: 16, flexDirection: "column", alignItems: "flex-start" }}>
              <div style={{ fontWeight: 900, fontSize: 13, opacity: 0.85, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Color{selectedColor ? `: ${selectedColor}` : ""}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {colorValues.map((color) => {
                  const isActive = selectedColor.toLowerCase() === color.toLowerCase();
                  const SWATCH = { red:'#ef4444', blue:'#3b82f6', green:'#22c55e', yellow:'#eab308', black:'#111827', white:'#f9fafb', pink:'#ec4899', purple:'#a855f7', orange:'#f97316', brown:'#92400e', grey:'#6b7280', gray:'#6b7280', navy:'#1e3a8a', beige:'#d4c8a8', teal:'#14b8a6' };
                  const swatch = SWATCH[color.toLowerCase()];
                  // Variants that match this colour
                  const colourVariants = variants.filter((v) =>
                    ((v.optionValues || []).find((ov) => ov.name === colorOptionName)?.value || "").toLowerCase() === color.toLowerCase()
                  );
                  const enabled = colourVariants.some((v) => v.isActive !== false && Number(v.stock || 0) > 0);
                  // Show a discount dot if ANY variant of this colour is discounted
                  const colourHasDiscount = colourVariants.some((v) => isVariantDiscounted(v));
                  return (
                    <button
                      key={color}
                      title={color}
                      disabled={!enabled}
                      onClick={() => {
                        setSelectedColor(color);
                        // Switch image to a representative variant of this colour, fallback to product images
                        const best = colourVariants.find((v) => Number(v.stock || 0) > 0) || colourVariants[0];
                        const raw = best?.images?.[0] || images[0] || "";
                        if (raw) setActiveImg(makeAbsolute(typeof raw === "string" ? raw : (raw?.url || raw?.path || "")));
                      }}
                      style={{
                        position: "relative",
                        padding: swatch ? 0 : "6px 12px",
                        width: swatch ? 36 : "auto",
                        height: swatch ? 36 : "auto",
                        minWidth: swatch ? 36 : "auto",
                        borderRadius: swatch ? "50%" : 8,
                        background: swatch || "rgba(255,255,255,0.06)",
                        color: swatch ? "transparent" : "inherit",
                        border: isActive ? `3px solid #8b5cf6` : `2px solid rgba(255,255,255,0.15)`,
                        cursor: enabled ? "pointer" : "not-allowed",
                        opacity: enabled ? 1 : 0.45,
                        fontWeight: 700,
                      }}
                    >
                      {!swatch && color}
                      {colourHasDiscount && (
                        <span style={{
                          position: "absolute", top: -6, right: -6,
                          width: 14, height: 14, borderRadius: "50%",
                          background: "#f97316", border: "2px solid #0f172a",
                        }} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Size Selector */}
          {sizeValues.length > 0 && (
            <div className="pdu-row" style={{ marginTop: 12, flexDirection: "column", alignItems: "flex-start" }}>
              <div style={{ fontWeight: 900, fontSize: 13, opacity: 0.85, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Size{selectedSize ? `: ${selectedSize}` : ""}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {sizeValues.map((size) => {
                  const isActive = selectedSize.toLowerCase() === size.toLowerCase();
                  // Find the variant for the current colour + this size
                  const sizeVariant = variants.find((v) => {
                    const c = (v.optionValues || []).find((ov) => ov.name === colorOptionName)?.value || "";
                    const s = (v.optionValues || []).find((ov) => ov.name === sizeOptionName)?.value || "";
                    const colorOk = !colorOptionName || !selectedColor || c.toLowerCase() === selectedColor.toLowerCase();
                    return colorOk && s.toLowerCase() === size.toLowerCase();
                  });
                  const enabled = sizeVariant && sizeVariant.isActive !== false && Number(sizeVariant.stock || 0) > 0;
                  const sizeDiscounted = isVariantDiscounted(sizeVariant);
                  const sizePct = variantPctFor(sizeVariant);
                  return (
                    <button
                      key={size}
                      disabled={!enabled}
                      onClick={() => setSelectedSize(size)}
                      style={{
                        position: "relative",
                        minWidth: 48,
                        padding: "8px 16px",
                        borderRadius: 8,
                        background: isActive ? "#8b5cf6" : "rgba(255,255,255,0.06)",
                        color: isActive ? "#fff" : "inherit",
                        border: isActive
                          ? "2px solid #8b5cf6"
                          : (sizeDiscounted ? "2px solid #f97316" : "2px solid rgba(255,255,255,0.15)"),
                        fontSize: 14,
                        fontWeight: isActive ? 800 : 600,
                        cursor: enabled ? "pointer" : "not-allowed",
                        opacity: enabled ? 1 : 0.4,
                      }}
                    >
                      {size}
                      {sizeDiscounted && (
                        <span style={{
                          position: "absolute", top: -8, right: -8,
                          fontSize: 9, fontWeight: 900, color: "#fff",
                          background: "#f97316", padding: "1px 4px", borderRadius: 6,
                          lineHeight: 1.3,
                        }}>
                          -{sizePct}%
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="pdu-row" style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 900, opacity: 0.9 }}>Quantity</div>
            <div className="pdu-qty">
              <button className="pdu-qtybtn" onClick={dec} type="button">
                −
              </button>
              <div className="pdu-qtybox">{qtySafe}</div>
              <button className="pdu-qtybtn" onClick={inc} type="button">
                +
              </button>
            </div>
          </div>

          {/* ✅ In Stock Quantity (variant-aware) */}
          <div className="pdu-row" style={{ marginTop: 8 }}>
            <div style={{ fontWeight: 900, opacity: 0.85, fontSize: 14 }}>In Stock</div>
            <div style={{ fontWeight: 1000, opacity: 0.9, fontSize: 14 }}>
              {variants.length > 0
                ? (selectedVariant
                    ? `${safeNum(selectedVariant.stock, 0)} units${selectedVariant.optionValues?.length
                        ? ` (${selectedVariant.optionValues.map(o => o.value).join(" / ")})`
                        : ""}`
                    : "Select options to see stock")
                : `${safeNum(product?.quantity, 0)} units`}
            </div>
          </div>

          <div className="pdu-desc">
            {String(product?.description || "").trim() ? product.description : "No description."}
          </div>

          {/* Preview Notice - Shop owners see how product looks to customers */}
          <div className="pdu-preview-notice">
            <div className="pdu-preview-icon">👁️</div>
            <div className="pdu-preview-text">
              <strong>Product Preview</strong>
              <span>This is how your product will appear to customers</span>
            </div>
          </div>

          <div className="pdu-small">Product ID: {pid}</div>

          {pid ? (
            <div style={{ marginTop: 14 }}>
              <ProductReviews productId={pid} defaultOpen={false} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
